//claud ai conversation link
//https://claude.ai/chat/f0d795b6-ba03-4c8a-adcd-3b4895ce34a2

import {RRule, rrulestr} from 'rrule'
import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'
import timezonePlugin from 'dayjs/plugin/timezone'

dayjs.extend(utcPlugin)
dayjs.extend(timezonePlugin)

// The occurrence types that repeat, and so take an end (UNTIL/COUNT).
// 'one_time_schedule' — and anything unrecognised, which generateRRule turns
// into a single occurrence — does not.
export const RECURRING_TYPES = ['daily', 'weekly_on_sunday', 'every_weekday', 'custom']

// The instant at which `date` ('YYYY-MM-DD') shows `time` ('HH:mm' or 'HH:mm:ss') on a wall clock in `zone`.
const zonedInstant = (date, time, zone) => dayjs.tz(`${date} ${time}`, zone).toDate()

const pad = n => String(n).padStart(2, '0')

// ─────────────────────────────────────────────────────────────────────────────
// GENERATE  →  form values  ➜  RRULE string
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates an RRULE string from the recurring schedule form data.
 *
 * Form fields used:
 *  - occurrence_type       : 'one_time_schedule' | 'daily' | 'weekly_on_sunday' | 'every_weekday' | 'custom'
 *  - occurrence_number     : number  (custom only) — repeat interval
 *  - occurrence_period     : 'day' | 'week' | 'month'  (custom only)
 *  - repeat_on_monday … repeat_on_sunday : boolean  (custom + week period only)
 *  - start_time            : string 'HH:mm'
 *  - end_time              : string 'HH:mm'  (returned as dtend, not in RRULE)
 *  - occurrence_start_date : Dayjs | Date | string
 *  - occurrence_ends       : 'on' | 'after' | null  (ignored for one_time_schedule)
 *  - occurrence_end_date   : Dayjs | Date | string  (when occurrence_ends === 'on')
 *  - occurrence_total      : number  (when occurrence_ends === 'after')
 *
 * @param {object}      formValues
 * @param {string|null} timezone  - IANA zone (e.g. 'America/Chicago') the schedule's times are wall-clock times in.
 *   DTSTART and UNTIL are then the UTC instants of those wall-clock times in that zone, whatever zone the browser is in.
 *   Without one (undefined, null or ''), they are built in the browser's zone.
 *
 * Returns: { rrule: string, dtstart: Date, dtend: Date | null }  (dtstart/dtend are the instants DTSTART and the end time stand for)
 */
export function generateRRule(formValues, timezone = null) {
    const zone = timezone || null
    const {
        occurrence_type,
        occurrence_number,
        occurrence_period,
        repeat_on_monday,
        repeat_on_tuesday,
        repeat_on_wednesday,
        repeat_on_thursday,
        repeat_on_friday,
        repeat_on_saturday,
        repeat_on_sunday,
        start_time,
        end_time,
        occurrence_start_date,
        occurrence_ends,
        occurrence_end_date,
        occurrence_total,
    } = formValues

    // ── DTSTART ──────────────────────────────────────────────────────────────
    // The start date is the calendar date that was picked (or today, in the schedule's zone when it has one).
    const startDate = occurrence_start_date ? dayjs(occurrence_start_date) : zone ? dayjs().tz(zone) : dayjs()
    const startDay = startDate.format('YYYY-MM-DD')
    // A wall-clock time on the start date: in the schedule's zone, else in the browser's.
    const onStartDate = (hour, minute) =>
        zone ? zonedInstant(startDay, `${pad(hour)}:${pad(minute)}`, zone) : startDate.hour(hour).minute(minute).second(0).millisecond(0).toDate()

    const [startHour = 0, startMin = 0] = (start_time ?? '00:00').split(':').map(Number)
    const dtstart = onStartDate(startHour, startMin)

    // ── DTEND (not part of RRULE spec, returned separately) ──────────────────
    let dtend = null
    if (end_time) {
        const [endHour = 0, endMin = 0] = end_time.split(':').map(Number)
        dtend = onStartDate(endHour, endMin)
    }

    // ── One-time → FREQ=DAILY;COUNT=1 (fires exactly once) ──────────────────
    if (occurrence_type === 'one_time_schedule') {
        const rule = new RRule({dtstart, freq: RRule.DAILY, count: 1})
        return {rrule: rule.toString(), dtstart, dtend}
    }

    const options = {dtstart}

    // ── UNTIL / COUNT ────────────────────────────────────────────────────────
    if (occurrence_ends === 'on' && occurrence_end_date) {
        // The whole last day, in the schedule's zone.
        options.until = zone
            ? zonedInstant(dayjs(occurrence_end_date).format('YYYY-MM-DD'), '23:59:59', zone)
            : dayjs(occurrence_end_date).endOf('day').toDate()
    } else if (occurrence_ends === 'after' && occurrence_total) {
        options.count = Number(occurrence_total)
    }

    // ── Frequency mapping ────────────────────────────────────────────────────
    switch (occurrence_type) {
        case 'daily':
            options.freq = RRule.DAILY
            options.interval = 1
            break

        case 'weekly_on_sunday':
            options.freq = RRule.WEEKLY
            options.interval = 1
            options.byweekday = [RRule.SU]
            break

        case 'every_weekday':
            options.freq = RRule.WEEKLY
            options.interval = 1
            options.byweekday = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR]
            break

        case 'custom':
            options.interval = Number(occurrence_number) || 1

            switch (occurrence_period) {
                case 'day':
                    options.freq = RRule.DAILY
                    break

                case 'month':
                    options.freq = RRule.MONTHLY
                    break

                case 'week':
                default:
                    options.freq = RRule.WEEKLY

                    const selectedDays = []
                    if (repeat_on_monday) selectedDays.push(RRule.MO)
                    if (repeat_on_tuesday) selectedDays.push(RRule.TU)
                    if (repeat_on_wednesday) selectedDays.push(RRule.WE)
                    if (repeat_on_thursday) selectedDays.push(RRule.TH)
                    if (repeat_on_friday) selectedDays.push(RRule.FR)
                    if (repeat_on_saturday) selectedDays.push(RRule.SA)
                    if (repeat_on_sunday) selectedDays.push(RRule.SU)

                    if (selectedDays.length > 0) options.byweekday = selectedDays
                    break
            }
            break

        default:
            // Fallback — should never be reached
            const fallback = new RRule({dtstart, freq: RRule.DAILY, count: 1})
            return {rrule: fallback.toString(), dtstart, dtend}
    }

    const rule = new RRule(options)
    return {rrule: rule.toString(), dtstart, dtend}
}

// ─────────────────────────────────────────────────────────────────────────────
// PARSE  →  RRULE string  ➜  form values
// ─────────────────────────────────────────────────────────────────────────────

const WEEKDAY_MAP = {
    MO: 'monday',
    TU: 'tuesday',
    WE: 'wednesday',
    TH: 'thursday',
    FR: 'friday',
    SA: 'saturday',
    SU: 'sunday',
}

const WEEKDAY_CODES = Object.keys(WEEKDAY_MAP)

/**
 * Parses an RRULE string back into form-compatible values.
 *
 * @param {string|null} rruleString  - The full RRULE string (may include DTSTART line).
 * @param {string|null} endTime      - 'HH:mm' end time (stored outside RRULE, pass it in if you have it).
 * @param {string|null} timezone     - IANA zone the rule was built in (the schedule's own `timezone`). DTSTART and UNTIL
 *   are read back as wall-clock date/time in that zone, whatever zone the browser is in. Without one (undefined, null
 *   or ''), they are read in the browser's zone.
 *
 * Returns an object matching all form field names so you can pass it directly
 * into react-hook-form's `reset(parseRRule(rruleString, endTime))`.
 */
export function parseRRule(rruleString, endTime = null, timezone = null) {
    // ── Defaults (mirrors an empty form) ─────────────────────────────────────
    const result = {
        occurrence_type: 'one_time_schedule',
        occurrence_number: 1,
        occurrence_period: 'week',
        repeat_on_monday: false,
        repeat_on_tuesday: false,
        repeat_on_wednesday: false,
        repeat_on_thursday: false,
        repeat_on_friday: false,
        repeat_on_saturday: false,
        repeat_on_sunday: false,
        start_time: null,
        end_time: endTime,
        occurrence_start_date: null,
        occurrence_ends: null,
        occurrence_end_date: null,
        occurrence_total: null,
    }

    if (!rruleString) return result

    const rule = rrulestr(rruleString)
    const opts = rule.origOptions
    // An instant as a dayjs value whose fields read as wall-clock time in the schedule's zone (or the browser's).
    const wallClock = instant => (timezone ? dayjs(instant).tz(timezone) : dayjs(instant))

    // ── DTSTART → start_time + occurrence_start_date ─────────────────────────
    if (opts.dtstart) {
        const start = wallClock(opts.dtstart)
        result.occurrence_start_date = start.format('YYYY-MM-DD')
        result.start_time = start.format('HH:mm')
    }

    // ── One-time detection: FREQ=DAILY + COUNT=1 ─────────────────────────────
    if ((opts.freq === RRule.DAILY && opts.count === 1 && !opts.interval) || opts.interval === 1) {
        const byweekday = opts.byweekday ?? []
        if (byweekday.length === 0 && opts.count === 1) {
            result.occurrence_type = 'one_time_schedule'
            return result
        }
    }

    // ── UNTIL / COUNT → occurrence_ends ──────────────────────────────────────
    if (opts.until) {
        result.occurrence_ends = 'on'
        result.occurrence_end_date = wallClock(opts.until).format('YYYY-MM-DD')
    } else if (opts.count) {
        result.occurrence_ends = 'after'
        result.occurrence_total = opts.count
    }

    // ── Normalise byweekday to an array of 2-letter codes ────────────────────
    const byweekdayCodes = (opts.byweekday ?? []).map(wd => {
        return WEEKDAY_CODES[wd.weekday ?? wd]
    })

    const interval = opts.interval ?? 1

    // ── Detect occurrence_type ────────────────────────────────────────────────
    if (opts.freq === RRule.DAILY && interval === 1 && byweekdayCodes.length === 0) {
        result.occurrence_type = 'daily'
    } else if (opts.freq === RRule.WEEKLY && interval === 1 && byweekdayCodes.length === 1 && byweekdayCodes[0] === 'SU') {
        result.occurrence_type = 'weekly_on_sunday'
    } else if (
        opts.freq === RRule.WEEKLY &&
        interval === 1 &&
        byweekdayCodes.length === 5 &&
        ['MO', 'TU', 'WE', 'TH', 'FR'].every(d => byweekdayCodes.includes(d))
    ) {
        result.occurrence_type = 'every_weekday'
    } else {
        result.occurrence_type = 'custom'
        result.occurrence_number = interval

        switch (opts.freq) {
            case RRule.DAILY:
                result.occurrence_period = 'day'
                break
            case RRule.MONTHLY:
                result.occurrence_period = 'month'
                break
            case RRule.WEEKLY:
            default:
                result.occurrence_period = 'week'
                byweekdayCodes.forEach(code => {
                    const fieldName = WEEKDAY_MAP[code]
                    if (fieldName) result[`repeat_on_${fieldName}`] = true
                })
                break
        }
    }

    return result
}

// ─────────────────────────────────────────────────────────────────────────────
// Usage examples
// ─────────────────────────────────────────────────────────────────────────────
//
// ── Generate ─────────────────────────────────────────────────────────────────
//
//   generateRRule({ occurrence_type: 'one_time_schedule', start_time: '09:00', end_time: '17:00', occurrence_start_date: '2025-06-01' })
//   → { rrule: "DTSTART:20250601T090000Z\nRRULE:FREQ=DAILY;COUNT=1", dtstart: Date, dtend: Date }
//
//   generateRRule({ occurrence_type: 'daily', start_time: '09:00', occurrence_start_date: '2025-06-01', occurrence_ends: 'after', occurrence_total: 5 })
//   → "DTSTART:20250601T090000Z\nRRULE:FREQ=DAILY;COUNT=5"      (built in the browser's zone, here UTC)
//
//   The same in the agency's zone, whatever the browser's:
//   generateRRule({ …same values… }, 'America/Chicago')
//   → "DTSTART:20250601T140000Z\nRRULE:FREQ=DAILY;COUNT=5"      (09:00 CDT)
//
// ── Parse ─────────────────────────────────────────────────────────────────────
//
//   parseRRule("DTSTART:20250601T090000Z\nRRULE:FREQ=DAILY;COUNT=1", "17:00")
//   → { occurrence_type: 'one_time_schedule', start_time: '09:00', end_time: '17:00', occurrence_start_date: '2025-06-01', … }
//
//   parseRRule("DTSTART:20250601T140000Z\nRRULE:FREQ=DAILY;COUNT=1", "17:00", 'America/Chicago')
//   → { occurrence_type: 'one_time_schedule', start_time: '09:00', end_time: '17:00', occurrence_start_date: '2025-06-01', … }
//
//   parseRRule("DTSTART:20250601T090000Z\nRRULE:FREQ=WEEKLY;INTERVAL=2;BYDAY=MO,FR;COUNT=10", "17:00")
//   → { occurrence_type: 'custom', occurrence_number: 2, occurrence_period: 'week',
//       repeat_on_monday: true, repeat_on_friday: true, occurrence_ends: 'after', occurrence_total: 10, … }
